import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Plus, Trash2, Loader2, MapPin, DollarSign, X } from "lucide-react";
import PageHead from "@/components/PageHead";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const COLUMNS = ["Saved", "Applied", "Screening", "Interview", "Offer", "Rejected"];

const TONE = {
  Saved: "border-t-aqua",
  Applied: "border-t-electric",
  Screening: "border-t-violet",
  Interview: "border-t-coral",
  Offer: "border-t-lime",
  Rejected: "border-t-muted-foreground",
};

export default function ApplicationTracker() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Form State for new application
  const [formData, setFormData] = useState({
    company: "",
    role: "",
    status: "Saved",
    salary: "$120k",
    location: "Remote",
    notes: "",
  });

  const fetchApplications = async () => {
    try {
      const { data } = await api.get("/applications");
      setApps(data?.applications || []);
    } catch (err) {
      toast.error("Could not fetch applications.");
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.company.trim() || !formData.role.trim()) {
      toast.error("Company and Role are required.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/applications", formData);
      if (data?.application) {
        setApps((prev) => [data.application, ...prev]);
        toast.success(`Added ${formData.role} at ${formData.company}`);
        setIsModalOpen(false);
        setFormData({
          company: "",
          role: "",
          status: "Saved",
          salary: "$120k",
          location: "Remote",
          notes: "",
        });
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to add application");
    } finally {
      setCreating(false);
    }
  };

  const advanceStage = async (card) => {
    const currentIdx = COLUMNS.indexOf(card.status);
    if (currentIdx < 0 || currentIdx >= COLUMNS.length - 2) return;
    const nextStatus = COLUMNS[currentIdx + 1];

    // Optimistic UI update
    setApps((prev) =>
      prev.map((a) => (a.id === card.id ? { ...a, status: nextStatus } : a))
    );

    try {
      await api.patch(`/applications/${card.id}`, { status: nextStatus });
      toast.success(`${card.role} moved to ${nextStatus}`);
    } catch (err) {
      toast.error("Could not update stage.");
      fetchApplications();
    }
  };

  const deleteApplication = async (appId) => {
    setApps((prev) => prev.filter((a) => a.id !== appId));
    try {
      await api.delete(`/applications/${appId}`);
      toast.success("Application removed");
    } catch (err) {
      toast.error("Could not delete application.");
      fetchApplications();
    }
  };

  // Group applications by status column
  const grouped = COLUMNS.reduce((acc, col) => {
    acc[col] = apps.filter((a) => (a.status || "Saved").toLowerCase() === col.toLowerCase());
    return acc;
  }, {});

  return (
    <div>
      <PageHead
        label="Application Tracker"
        title="Know where every application stands"
        sub="From saved to offer. Live cloud-synced Kanban board tracking your career journey."
      >
        <button
          onClick={() => setIsModalOpen(true)}
          className="cv-btn-primary text-sm flex items-center gap-1.5"
          data-testid="add-application-btn"
        >
          <Plus className="h-4 w-4" /> Add Application
        </button>
      </PageHead>

      {loading ? (
        <div className="flex min-h-[300px] items-center justify-center rounded-3xl border border-dashed border-border">
          <Loader2 className="h-6 w-6 animate-spin text-electric" />
          <span className="ml-2 text-xs text-muted-foreground">Loading your applications…</span>
        </div>
      ) : (
        <div className="-mx-5 overflow-x-auto px-5 pb-4 sm:mx-0 sm:px-0">
          <div className="flex min-w-max gap-4">
            {COLUMNS.map((col) => {
              const columnCards = grouped[col] || [];
              return (
                <div
                  key={col}
                  className={`w-64 rounded-2xl border border-t-4 border-border bg-secondary/40 p-3.5 ${TONE[col]}`}
                >
                  <p className="mb-3 flex items-center justify-between text-sm font-bold">
                    <span>{col}</span>
                    <span className="rounded-full bg-card px-2 py-0.5 text-xs font-semibold text-muted-foreground border border-border">
                      {columnCards.length}
                    </span>
                  </p>

                  <div className="space-y-2.5">
                    <AnimatePresence mode="popLayout">
                      {columnCards.map((c) => (
                        <motion.div
                          key={c.id}
                          layout
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          className="group rounded-xl border border-border bg-card p-3.5 shadow-xs transition-shadow hover:shadow-card"
                          data-testid={`tracker-card-${c.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-foreground truncate">{c.role}</p>
                              <p className="text-xs font-semibold text-electric truncate">{c.company}</p>
                              <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                                {c.location && (
                                  <span className="flex items-center gap-0.5">
                                    <MapPin className="h-3 w-3 text-muted-foreground" /> {c.location}
                                  </span>
                                )}
                                {c.salary && (
                                  <span className="flex items-center gap-0.5">
                                    <DollarSign className="h-3 w-3 text-lime" /> {c.salary}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                onClick={() => deleteApplication(c.id)}
                                className="rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>

                              {COLUMNS.indexOf(col) < COLUMNS.length - 2 && (
                                <button
                                  onClick={() => advanceStage(c)}
                                  className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-electric"
                                  title={`Advance to ${COLUMNS[COLUMNS.indexOf(col) + 1]}`}
                                  data-testid={`advance-${c.id}`}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {columnCards.length === 0 && (
                      <p className="py-8 text-center text-xs text-muted-foreground border border-dashed border-border/60 rounded-xl">
                        No applications
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Application Dialog Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Job Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Company Name *</label>
              <input
                type="text"
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="e.g. Stripe, Linear, Google"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-electric"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground">Role Title *</label>
              <input
                type="text"
                required
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder="e.g. Senior Product Manager"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-electric"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground">Initial Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-electric"
                >
                  {COLUMNS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-muted-foreground">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="e.g. Remote, London"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-electric"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-muted-foreground">Target Salary / Comp</label>
              <input
                type="text"
                value={formData.salary}
                onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                placeholder="e.g. $140k"
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-electric"
              />
            </div>

            <DialogFooter className="mt-5">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="cv-btn-ghost border border-border px-4 text-xs"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="cv-btn-primary px-5 text-xs flex items-center gap-1.5"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                Add to Tracker
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
